/* eslint-disable class-methods-use-this */
import secp256k1 from 'secp256k1';
import { Keccak } from 'sha3';
import { ml_dsa87 } from '@noble/post-quantum/ml-dsa';
import { randomBytes } from '@noble/post-quantum/utils';
import { p256 } from '@noble/curves/p256';
import { areEqualBinaries, binaryToHex } from './binaryUtils';
import { getSubtle } from '@/crypto';
export const KEY_TYPES = [
    'ECDSA_SECP256K1_EVM',
    'ECDSA_SECP256R1',
    'ML_DSA_87'
];
const ECDSA_SECP256K1_EVM_SIGN_MAGIC = '\u0019Ethereum Signed Message:\n';
export class SigningUtil {
    static getInstance(type) {
        switch (type) {
            case 'ECDSA_SECP256K1_EVM':
                return new EcdsaSecp256k1Evm();
            case 'ECDSA_SECP256R1':
                return new EcdsaSecp256r1();
            case 'ML_DSA_87':
                return new MlDsa87();
            default:
                throw new Error(`Unknown key pair type: ${type}`);
        }
    }
}
/**
 * EVM compatible ECDSA signing scheme using keccak hash, magic bytes, and secp256k1 curve.
 */
export class EcdsaSecp256k1Evm extends SigningUtil {
    generateKeyPair() {
        const privateKey = randomBytes(32);
        const publicKey = secp256k1.publicKeyCreate(privateKey, false);
        return {
            // Return address as 'publicKey'
            publicKey: this.publicKeyToAddress(publicKey),
            privateKey,
        };
    }
    keccakHash(message, useEthereumMagic = true) {
        const keccak = new Keccak(256);
        keccak.update(useEthereumMagic ? Buffer.concat([
            Buffer.from(ECDSA_SECP256K1_EVM_SIGN_MAGIC + message.length),
            message
        ]) : Buffer.from(message));
        return keccak.digest('binary');
    }
    recoverPublicKey(signature, payload) {
        const signatureBuffer = Buffer.from(signature);
        const recoveryId = signatureBuffer.readUInt8(signatureBuffer.length - 1) - 27;
        return secp256k1.ecdsaRecover(signatureBuffer.subarray(0, signatureBuffer.length - 1), recoveryId, this.keccakHash(payload), false, Buffer.alloc);
    }
    async createSignature(payload, privateKey) {
        const msgHash = this.keccakHash(payload);
        const sigObj = secp256k1.ecdsaSign(msgHash, privateKey);
        const result = Buffer.alloc(sigObj.signature.length + 1, Buffer.from(sigObj.signature));
        result.writeInt8(27 + sigObj.recid, result.length - 1);
        return result;
    }
    publicKeyToAddress(publicKey) {
        if (publicKey.length !== 65) {
            throw new Error(`Expected 65 bytes (an ECDSA uncompressed public key with header byte). Got length: ${publicKey.length}`);
        }
        const pubKeyWithoutFirstByte = publicKey.subarray(1, publicKey.length);
        const keccak = new Keccak(256);
        keccak.update(Buffer.from(pubKeyWithoutFirstByte));
        const hashOfPubKey = keccak.digest('binary');
        return hashOfPubKey.subarray(12, hashOfPubKey.length);
    }
    recoverSignerUserId(signature, payload) {
        const publicKey = this.recoverPublicKey(signature, payload);
        return this.publicKeyToAddress(publicKey);
    }
    async verifySignature(expectedUserId, payload, signature) {
        try {
            const recoveredAddress = this.recoverSignerUserId(signature, payload);
            return areEqualBinaries(recoveredAddress, expectedUserId);
        }
        catch {
            return false;
        }
    }
    assertValidKeyPair(address, privateKey) {
        const computedPublicKey = secp256k1.publicKeyCreate(privateKey, false);
        const computedAddress = this.publicKeyToAddress(computedPublicKey);
        if (!areEqualBinaries(address, computedAddress)) {
            throw new Error(`Given private key is for a different address! Given: ${binaryToHex(address)}, Computed: ${binaryToHex(computedAddress)}`);
        }
    }
}
/**
 * Signing scheme using ECDSA with secp256r1 curve and SHA-256, natively supported by browsers
 */
export class EcdsaSecp256r1 extends SigningUtil {
    generateKeyPair(compressPublicKey = true) {
        const privateKey = randomBytes(32);
        const publicKey = this.getPublicKeyFromPrivateKey(privateKey, compressPublicKey);
        return {
            publicKey,
            privateKey,
        };
    }
    isCompressedPublicKey(publicKey) {
        return publicKey.length === 33;
    }
    isUncompressedPublicKey(publicKey) {
        return publicKey.length === 65;
    }
    toBase64Url(base64) {
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
    getPublicKeyFromPrivateKey(privateKey, compressed = true) {
        return p256.getPublicKey(privateKey, compressed);
    }
    getUncompressedPublicKey(publicKey) {
        if (this.isCompressedPublicKey(publicKey)) {
            // Decode compressed public key to an elliptic curve point
            const point = p256.ProjectivePoint.fromHex(publicKey);
            // Convert the point to an uncompressed public key
            return point.toRawBytes(false);
        }
        // No-op if called with already uncompressed key
        if (this.isUncompressedPublicKey(publicKey)) {
            return publicKey;
        }
        throw new Error(`Unexpected public key length: ${publicKey.length}`);
    }
    privateKeyToJWK(privateKey) {
        const publicKey = this.getPublicKeyFromPrivateKey(privateKey, false);
        // uncompressed publicKey = [header (1 byte), x (32 bytes), y (32 bytes)
        const x = publicKey.subarray(1, 33);
        const y = publicKey.subarray(33);
        /**
         * Warning, there are some platform-specific differences. Logging observations here:
         * - buffer.toString('base64url') works on Mac but NOT in Linux/CI
         * - importKey accepts base64 encoded variables on Mac but NOT in Linux/CI
         * For this reason, they must be base64url encoded AND we need to use our own
         * toBase64Url converter.
         */
        const xBase64 = Buffer.from(x).toString('base64');
        const yBase64 = Buffer.from(y).toString('base64');
        const privateKeyBase64 = Buffer.from(privateKey).toString('base64');
        return {
            key_ops: ['sign'],
            ext: true,
            kty: 'EC',
            x: this.toBase64Url(xBase64),
            y: this.toBase64Url(yBase64),
            crv: 'P-256',
            d: this.toBase64Url(privateKeyBase64)
        };
    }
    /**
     * Pass the privateKey in JsonWebKey format for a slight performance gain.
     * You can convert raw keys to JWK using the privateKeyToJWK function.
     */
    async createSignature(payload, privateKey) {
        const subtleCrypto = getSubtle();
        const jwk = privateKey instanceof Uint8Array ? this.privateKeyToJWK(privateKey) : privateKey;
        /**
         * Stupidly, importKey does not support the 'raw' format. This means we need to
         * first compute the JWK from the raw key, and only then we can import and use it.
         */
        const key = await subtleCrypto.importKey('jwk', jwk, {
            name: 'ECDSA',
            namedCurve: 'P-256'
        }, false, ['sign']);
        const signature = await subtleCrypto.sign({
            name: 'ECDSA',
            hash: { name: 'SHA-256' }
        }, key, payload);
        return new Uint8Array(signature);
    }
    async publicKeyToCryptoKey(publicKey) {
        return getSubtle().importKey('raw', publicKey, {
            name: 'ECDSA',
            namedCurve: 'P-256'
        }, false, ['verify']);
    }
    async verifySignature(publicKey, payload, signature) {
        let key;
        try {
            key = await this.publicKeyToCryptoKey(publicKey);
        }
        catch (err) {
            // On some browsers (Safari), compressed keys are not supported for some reason!
            // If that might be the case, retry with an uncompressed key
            if (this.isCompressedPublicKey(publicKey)) {
                key = await this.publicKeyToCryptoKey(this.getUncompressedPublicKey(publicKey));
            }
            else {
                throw err;
            }
        }
        const isValid = await getSubtle().verify({
            name: 'ECDSA',
            hash: { name: 'SHA-256' }
        }, key, signature, payload);
        return isValid;
    }
    assertValidKeyPair(publicKey, privateKey) {
        if (privateKey.length !== 32) {
            throw new Error(`Expected a raw private key of 32 bytes. Maybe your key is in some encapsulating format?`);
        }
        if (!this.isCompressedPublicKey(publicKey) && !this.isUncompressedPublicKey(publicKey)) {
            throw new Error(`Expected a public key of either 33 bytes (compressed) or 65 bytes (uncompressed)!`);
        }
        const computedPublicKey = this.getPublicKeyFromPrivateKey(privateKey, publicKey.length === 33);
        if (!areEqualBinaries(computedPublicKey, publicKey)) {
            throw new Error(`Given private key is for a different public key! Given: ${binaryToHex(publicKey)}, Computed: ${binaryToHex(computedPublicKey)}`);
        }
    }
}
/**
 * Signing scheme using ML-DSA-87
 */
export class MlDsa87 extends SigningUtil {
    generateKeyPair() {
        const seed = randomBytes(32);
        const keys = ml_dsa87.keygen(seed);
        return {
            privateKey: keys.secretKey,
            publicKey: keys.publicKey,
        };
    }
    async createSignature(payload, privateKey, seed) {
        return ml_dsa87.sign(privateKey, payload, seed);
    }
    async verifySignature(publicKey, payload, signature) {
        return ml_dsa87.verify(publicKey, payload, signature);
    }
    assertValidKeyPair(publicKey, privateKey) {
        // Validity of key pair is tested by signing and validating something
        const payload = Buffer.from('data-to-sign');
        const signature = ml_dsa87.sign(privateKey, payload);
        if (!ml_dsa87.verify(publicKey, payload, signature)) {
            throw new Error(`The given ML-DSA public key and private key don't match!`);
        }
    }
}
